import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { BRAND } from "@/config/brand";
import { resolveSeo, getSiteOrigin, SEO_IMAGE } from "@/config/seo";

/**
 * Gestionnaire SEO central et route-aware.
 *
 * À chaque changement de route, il met à jour le titre, la description, les
 * mots-clés, la directive robots, l'URL canonique ainsi que les balises
 * Open Graph et Twitter Card. Toutes les valeurs gérées sont remplacées à
 * chaque navigation afin qu'aucune métadonnée d'une route précédente ne fuie
 * vers la route suivante. Une seule balise est conservée par name/property/rel.
 */

const MANAGED_TAGS: Array<{ attribute: "name" | "property"; key: string }> = [
  { attribute: "name", key: "description" },
  { attribute: "name", key: "keywords" },
  { attribute: "name", key: "robots" },
  { attribute: "name", key: "twitter:card" },
  { attribute: "name", key: "twitter:title" },
  { attribute: "name", key: "twitter:description" },
  { attribute: "name", key: "twitter:image" },
  { attribute: "property", key: "og:title" },
  { attribute: "property", key: "og:description" },
  { attribute: "property", key: "og:type" },
  { attribute: "property", key: "og:url" },
  { attribute: "property", key: "og:image" },
  { attribute: "property", key: "og:site_name" },
  { attribute: "property", key: "og:locale" },
];

function readMeta(attribute: "name" | "property", key: string): string | null {
  const element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  return element?.getAttribute("content") ?? null;
}

function setMeta(attribute: "name" | "property", key: string, content: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setCanonical(href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function SeoManager() {
  const { pathname } = useLocation();
  // Image de partage de référence : priorité aux balises gérées par la
  // plateforme déjà présentes, sinon l'image fournie explicitement.
  const baselineImage = useRef<string | null>(null);

  useEffect(() => {
    if (baselineImage.current === null) {
      const platformImage = readMeta("property", "og:image") ?? readMeta("name", "twitter:image");
      baselineImage.current = (platformImage && platformImage.trim()) || SEO_IMAGE;
    }

    const seo = resolveSeo(pathname);
    const origin = getSiteOrigin();
    const cleanPath = pathname.replace(/\/+$/, "");
    const canonical = `${origin}${cleanPath === "" ? "/" : cleanPath}`;
    const image = baselineImage.current;

    document.title = seo.title;

    setMeta("name", "description", seo.description);
    setMeta("name", "keywords", seo.keywords ?? "");
    setMeta("name", "robots", seo.robots ?? "index, follow");

    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:type", seo.ogType ?? "website");
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", image);
    setMeta("property", "og:site_name", BRAND.name);
    setMeta("property", "og:locale", "fr_FR");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", seo.title);
    setMeta("name", "twitter:description", seo.description);
    setMeta("name", "twitter:image", image);

    setCanonical(canonical);
  }, [pathname]);

  return null;
}

/** Liste des balises gérées, exposée pour l'audit SEO. */
export const MANAGED_SEO_TAGS = MANAGED_TAGS;